/**
 * Outil « Lanceur » — expériences et statistiques (pur).
 *
 * Pièce, dé, roue à trois secteurs réglables, et « dé mystère » (équilibré ou
 * truqué, à démasquer en lançant). Les lancers sont gardés un par un (au plus
 * MAX_THROWS) pour tracer la fréquence d'une issue au fil des lancers.
 */

import { rational, type ExactNumber } from '../../ui/exact';
import { fluctuationInterval, pickWeighted } from '../../ui/random';

export type ExperimentId = 'coin' | 'die' | 'wheel' | 'mystery';

export interface Issue {
  label: string;
  /** chances (entier) : la probabilité est weight / total */
  weight: number;
  color: string;
}

export const MAX_THROWS = 100_000;
export const BATCHES = [1, 10, 100, 1000, 10000] as const;

export const WHEEL_COLORS = [
  { label: 'Rouge', color: 'var(--color-error-500)' },
  { label: 'Bleu', color: 'var(--color-primary-500)' },
  { label: 'Vert', color: 'var(--color-success-500)' },
] as const;

const DIE_COLOR = 'var(--color-primary-500)';

/** Dé mystère : équilibré, ou une face a 1 chance sur 3 (poids 5 contre 2). */
export interface Mystery {
  loaded: boolean;
  /** face favorisée (1 à 6) si truqué */
  face: number;
}

export function pickMystery(rng: () => number): Mystery {
  return { loaded: rng() < 0.5, face: 1 + Math.min(5, Math.floor(rng() * 6)) };
}

export function issuesOf(exp: ExperimentId, wheel: readonly number[], mystery: Mystery): Issue[] {
  switch (exp) {
    case 'coin':
      return [
        { label: 'Pile', weight: 1, color: 'var(--color-primary-500)' },
        { label: 'Face', weight: 1, color: 'var(--color-accent-500)' },
      ];
    case 'die':
      return [1, 2, 3, 4, 5, 6].map((f) => ({ label: String(f), weight: 1, color: DIE_COLOR }));
    case 'wheel':
      return WHEEL_COLORS.map((c, i) => ({
        label: c.label,
        weight: wheel[i] ?? 1,
        color: c.color,
      }));
    case 'mystery':
      return [1, 2, 3, 4, 5, 6].map((f) => ({
        label: String(f),
        weight: mystery.loaded ? (f === mystery.face ? 5 : 2) : 1,
        color: DIE_COLOR,
      }));
  }
}

/** Probabilité exacte de chaque issue. */
/**
 * Probabilité exacte de chaque issue, toujours en FRACTION (« 1/2 », pas
 * « 0,5 » : au collège, « 1 chance sur 2 ») ; `decimal` la donne en décimal
 * quand il tombe juste (0,5 ; 0,4).
 */
export function probabilities(
  issues: readonly Issue[],
): Array<ExactNumber & { decimal: string | null }> {
  const total = issues.reduce((s, i) => s + i.weight, 0);
  return issues.map((i) => fraction(i.weight, total));
}

function gcd(a: number, b: number): number {
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

/** p/q réduite, écrite en fraction ; `decimal` si q = 2ⁱ5ʲ. */
export function fraction(p: number, q: number): ExactNumber & { decimal: string | null } {
  const g = gcd(p, q);
  const [a, b] = [p / g, q / g];
  const asDecimal = rational(a, b);
  const decimal = asDecimal.plain.includes('/') ? null : asDecimal.plain;
  if (b === 1) return { ...asDecimal, decimal: null };
  return {
    plain: `${a}/${b}`,
    tex: `\\frac{${a}}{${b}}`,
    atoms: [String(a), '÷', String(b)],
    value: a / b,
    decimal,
  };
}

/** Historique des lancers : un octet par lancer (indice de l'issue). */
export interface Throws {
  outcomes: Uint8Array;
  n: number;
  counts: number[];
}

export function emptyThrows(issueCount: number): Throws {
  return { outcomes: new Uint8Array(1024), n: 0, counts: Array(issueCount).fill(0) };
}

/** Ajoute `count` lancers (sans dépasser MAX_THROWS) ; renvoie un nouvel état. */
export function throwMany(
  state: Throws,
  issues: readonly Issue[],
  count: number,
  rng: () => number,
): Throws {
  const room = Math.max(0, MAX_THROWS - state.n);
  const k = Math.min(count, room);
  if (k === 0) return state;
  let outcomes = state.outcomes;
  if (state.n + k > outcomes.length) {
    const bigger = new Uint8Array(Math.min(MAX_THROWS, Math.max(outcomes.length * 2, state.n + k)));
    bigger.set(outcomes.subarray(0, state.n));
    outcomes = bigger;
  }
  const weights = issues.map((i) => i.weight);
  const counts = [...state.counts];
  for (let i = 0; i < k; i++) {
    const r = pickWeighted(weights, rng);
    outcomes[state.n + i] = r;
    counts[r]!++;
  }
  return { outcomes, n: state.n + k, counts };
}

/** Fréquence de l'issue `issue` après chacun des lancers demandés (rangs 1…n). */
export function runningFrequency(state: Throws, issue: number, at: readonly number[]): number[] {
  const out: number[] = [];
  let hits = 0;
  let j = 0;
  for (let i = 0; i < state.n && j < at.length; i++) {
    if (state.outcomes[i] === issue) hits++;
    while (j < at.length && at[j] === i + 1) {
      out.push(hits / (i + 1));
      j++;
    }
  }
  return out;
}

/**
 * Verdict du dé mystère, expliqué par l'intervalle de fluctuation : la face
 * dont la fréquence s'écarte le plus de 1/6.
 */
export function mysteryEvidence(state: Throws): {
  face: number;
  freq: number;
  interval: [number, number];
  outside: boolean;
  /** au moins 25 lancers : l'intervalle p ± 1/√n a un sens */
  usable: boolean;
} | null {
  if (state.n === 0) return null;
  // l'hypothèse est une face FAVORISÉE : on regarde la plus fréquente
  let best = 0;
  for (let f = 1; f < 6; f++) if (state.counts[f]! > state.counts[best]!) best = f;
  const freq = state.counts[best]! / state.n;
  const interval = fluctuationInterval(1 / 6, state.n);
  return {
    face: best + 1,
    freq,
    interval,
    outside: freq < interval[0] || freq > interval[1],
    usable: state.n >= FLUCTUATION_MIN,
  };
}

/** taille d'échantillon à partir de laquelle on utilise p ± 1/√n */
export const FLUCTUATION_MIN = 25;

/** Dé mystère : assez de lancers pour trancher sereinement ? */
export const MYSTERY_ENOUGH = 300;
